import React, { useCallback, useEffect, useMemo, useState } from 'react'
import * as walletService from '../../services/walletService'
import {
  getMyAmbTransactions,
  type AmbHistoryMode,
  type AmbHistoryRoundEvent,
  type AmbHistoryRoundSummary,
} from '../../services/ambHistoryService'
import { HistoryCollapseCard, type HistoryCardLeftIcon, type HistoryDetailRow } from './HistoryCollapseCard'

const PAGE_SIZE = 20

type ReportMode = Exclude<AmbHistoryMode, 'all'>
type DatePreset = 'all' | 'today' | '7d' | '30d'

const SETTLED_STATUS_OPTIONS = [
  { value: 'all', label: 'ทั้งหมด' },
  { value: 'SETTLED', label: 'จบแล้ว' },
  { value: 'COMPLETED', label: 'สำเร็จ' },
  { value: 'REFUND', label: 'คืนเงิน' },
  { value: 'VOID', label: 'ยกเลิก/โมฆะ' },
  { value: 'CANCELLED', label: 'ยกเลิก' },
]

const PENDING_STATUS_OPTIONS = [
  { value: 'all', label: 'ทั้งหมด' },
  { value: 'OPEN', label: 'กำลังเล่น' },
  { value: 'WAITING', label: 'รอผล' },
]

const STATUS_LABEL: Record<string, string> = {
  WAITING: 'รอผล',
  OPEN: 'กำลังเล่น',
  SETTLED: 'จบแล้ว',
  COMPLETED: 'สำเร็จ',
  REFUND: 'คืนเงิน',
  REJECT: 'ไม่สำเร็จ',
  VOID: 'โมฆะ',
  CANCELLED: 'ยกเลิก',
}

const TRANSACTION_TYPE_LABEL: Record<string, string> = {
  PLACE_BET: 'วางเดิมพัน',
  SETTLE_BET: 'สรุปผลเดิมพัน',
  CANCEL_BET: 'ยกเลิกเดิมพัน',
  ADJUST_BET: 'ปรับยอดเดิมพัน',
  ROLLBACK: 'ย้อนรายการ',
  WIN_REWARD: 'รับรางวัล',
  PLACE_TIP: 'เดิมพันพิเศษ',
  CANCEL_TIP: 'ยกเลิกเดิมพันพิเศษ',
  VOID_SETTLED: 'โมฆะผลเดิมพัน',
  ADJUST_BALANCE: 'ปรับยอด',
  CHECK_BALANCE: 'ตรวจสอบยอด',
  SETTLE_SINGLE_STATE: 'สรุปผลเกม',
}

const PRESET_LABELS: Record<DatePreset, string> = {
  all: 'ทั้งหมด',
  today: 'วันนี้',
  '7d': '7 วัน',
  '30d': '30 วัน',
}

function getPresetDescription(preset: DatePreset): string {
  const range = getPresetRange(preset)
  if (preset === 'all') return 'ทุกช่วงเวลา'
  if (preset === 'today') return `วันนี้ (${range.startDate})`
  return `${PRESET_LABELS[preset]} (${range.startDate} ถึง ${range.endDate})`
}

const FILTER_PANEL_STYLE: React.CSSProperties = {
  background: 'rgba(248, 250, 252, 0.98)',
  border: '1px solid rgba(148, 163, 184, 0.35)',
  boxShadow: '0 12px 30px rgba(15, 23, 42, 0.18)',
}

const FILTER_INPUT_STYLE: React.CSSProperties = {
  background: '#ffffff',
  color: '#0f172a',
  border: '1px solid rgba(148, 163, 184, 0.45)',
}

const FILTER_MUTED_TEXT_STYLE: React.CSSProperties = {
  color: '#475569',
}

const FILTER_INACTIVE_BUTTON_STYLE: React.CSSProperties = {
  background: '#e2e8f0',
  color: '#0f172a',
  boxShadow: 'inset 0 0 0 1px rgba(148, 163, 184, 0.55)',
}

function formatBangkokDate(date: Date): string {
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone: 'Asia/Bangkok',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).formatToParts(date)
  const year = parts.find((part) => part.type === 'year')?.value || '1970'
  const month = parts.find((part) => part.type === 'month')?.value || '01'
  const day = parts.find((part) => part.type === 'day')?.value || '01'
  return `${year}-${month}-${day}`
}

function shiftBangkokDate(dateString: string, days: number): string {
  const date = new Date(`${dateString}T00:00:00+07:00`)
  date.setUTCDate(date.getUTCDate() + days)
  return formatBangkokDate(date)
}

function toBangkokApiDate(dateString: string, boundary: 'start' | 'end'): string {
  if (!dateString) return ''
  return boundary === 'start'
    ? `${dateString}T00:00:00.000+07:00`
    : `${dateString}T23:59:59.999+07:00`
}

function getPresetRange(preset: DatePreset): { startDate: string; endDate: string } {
  if (preset === 'all') {
    return { startDate: '', endDate: '' }
  }

  const todayBangkok = formatBangkokDate(new Date())

  if (preset === 'today') {
    return { startDate: todayBangkok, endDate: todayBangkok }
  }

  return {
    startDate: shiftBangkokDate(todayBangkok, preset === '7d' ? -6 : -29),
    endDate: todayBangkok,
  }
}

function formatDateTime(dateStr?: string | null): string {
  if (!dateStr) return '-'
  try {
    return new Date(dateStr).toLocaleString('th-TH', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    })
  } catch {
    return dateStr
  }
}

function formatMoney(value?: number | null): string {
  return walletService.formatCurrency(Number(value) || 0)
}

function getStatusLabel(status?: string): string {
  return STATUS_LABEL[String(status || '').toUpperCase()] || String(status || '-')
}

function getTransactionTypeLabel(type?: string): string {
  return TRANSACTION_TYPE_LABEL[String(type || '').toUpperCase()] || String(type || '-')
}

function getPrimaryTimestamp(summary: AmbHistoryRoundSummary, mode: ReportMode): string {
  if (mode === 'pending') {
    return formatDateTime(summary.placedAt || summary.createdAt)
  }
  return formatDateTime(summary.settledAt || summary.updatedAt || summary.createdAt)
}

function getLeftIcon(summary: AmbHistoryRoundSummary, mode: ReportMode): HistoryCardLeftIcon {
  const status = String(summary.status || '').toUpperCase()
  const netAmount = Number(summary.netAmount) || 0

  if (mode === 'pending' || status === 'WAITING' || status === 'OPEN') return 'pending'
  if (status === 'REFUND' || status === 'VOID' || status === 'CANCELLED') return 'info'
  if (netAmount > 0 || status === 'SETTLED' || status === 'COMPLETED') return 'success'
  return 'reject'
}

function buildSearchText(summary: AmbHistoryRoundSummary): string {
  return [
    summary.transactionId,
    summary.betId,
    summary.roundId,
    summary.productId,
    summary.gameCode,
    summary.gameName,
    getStatusLabel(summary.status),
    formatDateTime(summary.createdAt),
    ...(summary.transactionIds || []),
    ...(summary.events || []).map((event) => `${event.transactionType} ${event.status}`),
  ]
    .filter(Boolean)
    .join(' ')
    .toLowerCase()
}

function summarizeEvents(events: AmbHistoryRoundEvent[] = []): string {
  if (!Array.isArray(events) || events.length === 0) return '-'

  const lines = events.slice(0, 4).map((event, index) => {
    const typeLabel = getTransactionTypeLabel(event.transactionType)
    const win = Number(event.winAmount) || 0
    const bet = Number(event.amount) || 0
    const signedNet = Number(event.netAmount) || 0
    return `${index + 1}. ${typeLabel}\n   เดิมพัน ${formatMoney(bet)} · ชนะ ${formatMoney(win)} · สุทธิ ${signedNet > 0 ? '+' : ''}${formatMoney(signedNet)}`
  })

  const body = lines.join('\n')
  return events.length > 4 ? `${body}\n…และอีก ${events.length - 4} รายการ` : body
}

function getRoundHint(summary: AmbHistoryRoundSummary): string {
  if ((summary.zeroBetWinEventCount || 0) > 0 && (summary.amount || 0) > 0) {
    return `รอบนี้มีโบนัส/ผลย่อย ${summary.zeroBetWinEventCount} รายการ โดยยอดเดิมพันหลักถูกหักไปแล้วในรายการก่อนหน้า`
  }
  if ((summary.eventCount || 0) > 1) {
    return `รอบนี้มี ${summary.eventCount} เหตุการณ์จากเกมเดียวกัน จึงถูกรวมให้เหลือ 1 รายการ`
  }
  return '1 รายการนี้คือสรุปของ 1 รอบการเล่น'
}

function getRoundResultText(netAmount?: number | null): string {
  const value = Number(netAmount) || 0
  if (value > 0) {
    return `กำไรในรอบนี้ ${formatMoney(value)} บาท`
  }
  if (value < 0) {
    return `ขาดทุนในรอบนี้ ${formatMoney(Math.abs(value))} บาท`
  }
  return 'ผลลัพธ์รอบนี้ 0.00 บาท'
}

interface HistoryAmbReportProps {
  mode: ReportMode
}

const HistoryAmbReport: React.FC<HistoryAmbReportProps> = ({ mode }) => {
  const [roundSummaries, setRoundSummaries] = useState<AmbHistoryRoundSummary[]>([])
  const [total, setTotal] = useState(0)
  const [page, setPage] = useState(1)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [searchQuery, setSearchQuery] = useState('')
  const [statusFilter, setStatusFilter] = useState('all')
  const [datePreset, setDatePreset] = useState<DatePreset>('all')

  const { startDate, endDate } = useMemo(() => getPresetRange(datePreset), [datePreset])

  const title = mode === 'pending' ? 'รอผลการเล่นพนัน AMB' : 'ประวัติการเล่นพนัน AMB'
  const emptyText = mode === 'pending' ? 'ยังไม่มีรายการ AMB ที่รอผล' : 'ยังไม่มีประวัติการเล่น AMB'
  const statusOptions = mode === 'pending' ? PENDING_STATUS_OPTIONS : SETTLED_STATUS_OPTIONS

  const loadTransactions = useCallback(async (pageNum: number = 1) => {
    try {
      setLoading(true)
      setError(null)
      const result = await getMyAmbTransactions({
        mode,
        page: pageNum,
        limit: PAGE_SIZE,
        ...(statusFilter !== 'all' ? { status: statusFilter } : {}),
        ...(startDate ? { startDate: toBangkokApiDate(startDate, 'start') } : {}),
        ...(endDate ? { endDate: toBangkokApiDate(endDate, 'end') } : {}),
      })

      setRoundSummaries(result.roundSummaries || [])
      setTotal(result.pagination?.total || 0)
      setPage(result.pagination?.page || pageNum)
    } catch (e) {
      setError(e instanceof Error ? e.message : 'โหลดข้อมูลไม่สำเร็จ')
      setRoundSummaries([])
      setTotal(0)
    } finally {
      setLoading(false)
    }
  }, [endDate, mode, startDate, statusFilter])

  useEffect(() => {
    loadTransactions(1)
  }, [loadTransactions])

  const filteredTransactions = useMemo(() => {
    const q = searchQuery.trim().toLowerCase()
    if (!q) return roundSummaries
    return roundSummaries.filter((summary) => buildSearchText(summary).includes(q))
  }, [searchQuery, roundSummaries])

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE))

  const handlePresetChange = (preset: DatePreset) => {
    setDatePreset(preset)
  }

  const handleClearFilters = () => {
    setSearchQuery('')
    setStatusFilter('all')
    setDatePreset('all')
  }

  const handleCopyRef = async (refId: string) => {
    try {
      await navigator.clipboard.writeText(refId)
    } catch {
      // ignore clipboard failure
    }
  }

  return (
    <>
      <div className="c_quinary text-center">
        <h3 className="c_tertiary font_md">{title}</h3>
      </div>

      <div className="border_5 p-3 mt-3" style={FILTER_PANEL_STYLE}>
        <div className="row g-2">
          <div className="col-12">
            <div className="input-group border_5">
              <span className="input-group-text border_5" style={FILTER_INPUT_STYLE}>
                <i className="fas fa-search" />
              </span>
              <input
                type="text"
                className="form-control border_5"
                style={FILTER_INPUT_STYLE}
                placeholder="ค้นหาเกม, ผู้ให้บริการ, สถานะ, รหัสรายการ..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
              {searchQuery.trim() && (
                <button
                  type="button"
                  className="btn border_5"
                  style={FILTER_INPUT_STYLE}
                  onClick={() => setSearchQuery('')}
                >
                  <i className="fas fa-times" />
                </button>
              )}
            </div>
            <small style={FILTER_MUTED_TEXT_STYLE}>ค้นหาในรายการ AMB ที่โหลดอยู่ของหน้าปัจจุบัน</small>
          </div>

          <div className="col-12">
            <div className="d-flex flex-wrap gap-2">
              {(['all', 'today', '7d', '30d'] as DatePreset[]).map((preset) => (
                <button
                  key={preset}
                  type="button"
                  className={`border-0 px-2 py-1 border_5 ${datePreset === preset ? 'bg_gd_quaternary c_tertiary' : 'bg_op_secondary c_primary'}`}
                  style={datePreset === preset ? undefined : FILTER_INACTIVE_BUTTON_STYLE}
                  onClick={() => handlePresetChange(preset)}
                >
                  {PRESET_LABELS[preset]}
                </button>
              ))}
            </div>
          </div>

          <div className="col-12">
            <small style={FILTER_MUTED_TEXT_STYLE}>
              ช่วงเวลา: {getPresetDescription(datePreset)} · เลือกได้จากปุ่มด้านบนเท่านั้น
            </small>
          </div>

          <div className="col-12">
            <div className="d-flex flex-wrap gap-2">
              {statusOptions.map((option) => (
                <button
                  key={option.value}
                  type="button"
                  className={`border-0 px-2 py-1 border_5 ${statusFilter === option.value ? 'bg_gd_secondary c_tertiary' : 'bg_op_secondary c_primary'}`}
                  style={statusFilter === option.value ? undefined : FILTER_INACTIVE_BUTTON_STYLE}
                  onClick={() => setStatusFilter(option.value)}
                >
                  {option.label}
                </button>
              ))}
            </div>
          </div>

          <div className="col-12 d-flex gap-2">
            <button type="button" className="bg_gd_quaternary c_tertiary border_5 border-0 px-3 py-2" onClick={() => loadTransactions(1)}>
              <i className="fas fa-filter me-1" /> ใช้ตัวกรอง
            </button>
            <button
              type="button"
              className="border_5 border-0 px-3 py-2"
              style={FILTER_INACTIVE_BUTTON_STYLE}
              onClick={handleClearFilters}
            >
              ล้างตัวกรอง
            </button>
          </div>
        </div>
      </div>

      <div className="border_5 bg_secondary p-3 mt-2">
        <div className="d-flex flex-wrap align-items-center justify-content-between gap-2">
          <div>
            <div className="c_primary font_md">รายการที่แสดงในหน้านี้</div>
            <small className="c_tertiary">
              แสดงผลจากรายการที่โหลดอยู่ในหน้าปัจจุบัน และช่องค้นหาจะกรองเฉพาะรายการชุดนี้
            </small>
          </div>
          <div className="text-end">
            <div className="c_primary font_md">{filteredTransactions.length} รายการ</div>
            <small className="c_tertiary">ทั้งหมด {total} รายการ</small>
          </div>
        </div>
      </div>

      <div className="mt-3 d-flex flex-column gap-2">
        {loading && (
          <div className="border_5 bg_secondary p-3 text-center c_tertiary">
            <i className="fas fa-spinner fa-spin me-2" />
            กำลังโหลดประวัติการเล่น AMB...
          </div>
        )}

        {!loading && error && (
          <div className="border_5 bg_secondary p-3 text-center c_danger">{error}</div>
        )}

        {!loading && !error && filteredTransactions.length === 0 && (
          <div className="border_5 bg_secondary p-3 text-center c_tertiary">{emptyText}</div>
        )}

        {!loading && !error && filteredTransactions.map((transaction) => {
          const refId = transaction.roundId || transaction.transactionId || transaction._id
          const gameLabel = transaction.gameName || transaction.gameCode || '-'
          const providerLabel = transaction.productId || '-'
          const primaryTime = getPrimaryTimestamp(transaction, mode)
          const statusLabel = getStatusLabel(transaction.status)
          const netAmount = Number(transaction.netAmount) || 0
          const netTone: HistoryDetailRow['tone'] = netAmount > 0 ? 'positive' : netAmount < 0 ? 'negative' : 'default'
          const rows: HistoryDetailRow[] = [
            { label: 'สถานะ', value: statusLabel },
            { label: 'ประเภทรายการ', value: 'สรุปต่อ 1 รอบการเล่น' },
            { label: 'ผู้ให้บริการ', value: providerLabel },
            { label: 'เกม', value: gameLabel },
            { label: 'จำนวนเหตุการณ์ในรอบ', value: String(transaction.eventCount || 0) },
            { label: 'ยอดเดิมพันรวม', value: `${formatMoney(transaction.amount)} ${transaction.currency || 'THB'}` },
            { label: 'ยอดรับรวม', value: `${formatMoney(transaction.winAmount)} ${transaction.currency || 'THB'}` },
            {
              label: 'สุทธิรวม',
              value: `${formatMoney(transaction.netAmount)} ${transaction.currency || 'THB'}`,
              tone: netTone,
            },
            { label: 'กำไร/ขาดทุนในรอบ', value: getRoundResultText(transaction.netAmount), tone: netTone },
            { label: 'ยอดเงินก่อน', value: `${formatMoney(transaction.balanceBefore)} ${transaction.currency || 'THB'}` },
            { label: 'ยอดเงินหลัง', value: `${formatMoney(transaction.balanceAfter)} ${transaction.currency || 'THB'}` },
            { label: 'เวลาเริ่ม', value: formatDateTime(transaction.placedAt || transaction.createdAt) },
            { label: 'เวลาปิดรายการ', value: formatDateTime(transaction.settledAt || transaction.updatedAt) },
            { label: 'คำอธิบาย', value: getRoundHint(transaction), tone: 'muted' },
            { label: 'รายการย่อยในรอบ', value: summarizeEvents(transaction.events), tone: 'muted' },
            { label: 'Transaction หลัก', value: transaction.transactionId || '-', tone: 'muted' },
            { label: 'Bet ID หลัก', value: transaction.betId || '-', tone: 'muted' },
            { label: 'Round ID', value: transaction.roundId || '-', tone: 'muted' },
            { label: 'Game Code', value: transaction.gameCode || '-', tone: 'muted' },
          ]

          return (
            <HistoryCollapseCard
              key={transaction._id || refId}
              id={transaction._id || refId}
              leftIcon={getLeftIcon(transaction, mode)}
              refId={refId}
              onCopyRef={() => handleCopyRef(refId)}
              header={(
                <div className="history-amb-card-header pe-md-4">
                  <div className="history-amb-card-header__main">
                    <div className="c_primary font_sm">{gameLabel}</div>
                    <small className="c_tertiary d-block">
                      {providerLabel} · เดิมพันรวม {formatMoney(transaction.amount)} · {transaction.eventCount} เหตุการณ์
                    </small>
                    <small className="d-block c_tertiary">
                      {statusLabel} · ยอดก่อน {formatMoney(transaction.balanceBefore)} → หลัง {formatMoney(transaction.balanceAfter)}
                    </small>
                  </div>
                  <div className="history-amb-card-header__meta">
                    <div className="c_tertiary font_sm">{primaryTime}</div>
                    <small className={`${netAmount >= 0 ? 'c_success' : 'c_danger'}`}>
                      สุทธิ {formatMoney(transaction.netAmount)}
                    </small>
                  </div>
                </div>
              )}
              rows={rows}
            />
          )
        })}
      </div>

      {!loading && !error && totalPages > 1 && (
        <div className="d-flex align-items-center justify-content-between gap-2 mt-3">
          <button
            type="button"
            className="bg_op_secondary c_primary border_5 border-0 px-3 py-2"
            disabled={page <= 1}
            onClick={() => loadTransactions(page - 1)}
          >
            <i className="fas fa-chevron-left me-1" />
            ก่อนหน้า
          </button>
          <div className="c_tertiary">
            หน้า {page} / {totalPages}
          </div>
          <button
            type="button"
            className="bg_op_secondary c_primary border_5 border-0 px-3 py-2"
            disabled={page >= totalPages}
            onClick={() => loadTransactions(page + 1)}
          >
            ถัดไป
            <i className="fas fa-chevron-right ms-1" />
          </button>
        </div>
      )}
    </>
  )
}

export default HistoryAmbReport
